/* =================================================================================================
 * AND1 market selector, cart-safety guard and market recommendation banner.
 *
 * Owns:
 *  - The selector drawer (opened from any [data-market-selector-open] trigger; slide-in,
 *    focus trap, Esc and focus return come from the theme's side-drawer element).
 *  - The drawer's country -> language filtering over the Liquid-derived per-country
 *    dataset, and its guarded submit: a market switch with a non-empty cart warns
 *    before items are silently dropped (market exclusions).
 *  - A post-switch backstop notice if items were removed anyway.
 *  - The load-time confirmation gate: shown ONLY on a confident market mismatch
 *    (mismatch-only bail-outs run before any node is inserted). Blocking, with a
 *    blurred backdrop; Esc is the single escape hatch and writes the suppress key.
 *    It never redirects — confirming the current market just closes it.
 *
 * Conventions:
 *  - All fetches use window.Shopify.routes.root, never root-relative paths.
 *  - localStorage key and1_market_choice_v1 suppresses the banner permanently once the
 *    visitor has made any explicit choice (accept, dismiss, or manual selection).
 * ================================================================================================= */

(function () {
  'use strict';

  var CHOICE_KEY = 'and1_market_choice_v1';
  var SWITCH_KEY = 'and1_market_switch_v1';
  var SWITCH_MAX_AGE_MS = 60000;

  var configEl = document.querySelector('[data-market-selector-config]');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    return;
  }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var drawer = document.querySelector('[data-market-drawer]');
  var warning = document.querySelector('[data-market-cart-warning]');
  var gate = document.querySelector('[data-market-gate]');
  var notice = document.querySelector('[data-market-notice]');

  function countryEntry(code) {
    return (config.countries && config.countries[code]) || null;
  }

  /* ---------------------------------------------------------------- storage (fail quiet) */

  function readStorage(storage, key) {
    try {
      var raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (err) {
      return false;
    }
  }

  function removeStorage(storage, key) {
    try {
      storage.removeItem(key);
    } catch (err) {
      /* ignore */
    }
  }

  function persistChoice(country, language) {
    writeStorage(window.localStorage, CHOICE_KEY, {
      country: country || null,
      language: language || null,
      ts: Date.now()
    });
  }

  function pushEvent(name, payload) {
    var data = { event: name };
    for (var key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) data[key] = payload[key];
    }
    (window.dataLayer = window.dataLayer || []).push(data);
  }

  /* ---------------------------------------------------------------- focus trap (gate + warning) */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapTab(container, evt) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    var focusable = [];
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) focusable.push(nodes[i]);
    }
    if (!focusable.length) {
      evt.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (evt.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------- drawer open/close */

  /* Slide-in, backdrop, focus trap, Esc and focus return are all provided by the theme's
     side-drawer element; this only wires the trigger and mirrors aria-expanded. */
  document.addEventListener('click', function (evt) {
    var opener = evt.target.closest('[data-market-selector-open]');
    if (!opener || !drawer || typeof drawer.open !== 'function') return;
    evt.preventDefault();
    opener.setAttribute('aria-expanded', 'true');
    drawer.open(opener);
    pushEvent('market_selector_drawer_open', {
      current_country: config.currentCountry,
      current_market: config.currentMarket || null
    });
  });

  if (drawer) {
    drawer.addEventListener('on:market-drawer:before-close', function () {
      var triggers = document.querySelectorAll('[data-market-selector-open]');
      for (var i = 0; i < triggers.length; i += 1) triggers[i].setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('keydown', function (evt) {
    if (warning && !warning.hidden) {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        resolveWarning(false);
      } else if (evt.key === 'Tab') {
        trapTab(warning.querySelector('[role="alertdialog"]'), evt);
      }
      return;
    }
    if (gate && !gate.hidden) {
      /* Esc is the gate's only escape hatch: close and suppress permanently. */
      if (evt.key === 'Escape') {
        evt.preventDefault();
        dismissGate();
      } else if (evt.key === 'Tab') {
        trapTab(gate.querySelector('[role="dialog"]'), evt);
      }
    }
  });

  /* ---------------------------------------------------------------- cart safety */

  var warningResolve = null;

  function resolveWarning(proceed) {
    if (!warning) return;
    warning.hidden = true;
    var resolve = warningResolve;
    warningResolve = null;
    if (resolve) resolve(proceed);
  }

  function showCartWarning(droppedLines) {
    return new Promise(function (resolve) {
      if (!warning) {
        resolve(true);
        return;
      }
      var list = warning.querySelector('[data-market-cart-warning-items]');
      if (list) {
        list.textContent = '';
        droppedLines.forEach(function (line) {
          var item = document.createElement('li');
          item.textContent = line.title;
          list.appendChild(item);
        });
      }
      warningResolve = resolve;
      warning.hidden = false;
      var box = warning.querySelector('[role="alertdialog"]');
      if (box) box.focus();
    });
  }

  if (warning) {
    var confirmBtn = warning.querySelector('[data-market-cart-warning-confirm]');
    var cancelBtn = warning.querySelector('[data-market-cart-warning-cancel]');
    if (confirmBtn) confirmBtn.addEventListener('click', function () { resolveWarning(true); });
    if (cancelBtn) cancelBtn.addEventListener('click', function () { resolveWarning(false); });
  }

  /* Destination markets come from the Liquid-derived dataset (each country's
     market_code metafield via country.market) — never used for routing (the
     localization POST lets Shopify resolve the market), only for warning-off/
     suppression decisions. A blank or missing market_code fails open: no cart
     pre-check, no gate. The post-switch backstop catches any cart-warning miss. */
  function destinationMarketOf(countryCode) {
    var entry = countryEntry(countryCode);
    return entry && entry.market ? entry.market : null;
  }

  function fetchCartMarketData() {
    /* Section Rendering API wraps the section in <div id="shopify-section-...">, so the
       JSON is carried in a <script type="application/json"> tag and extracted from the
       parsed HTML — never JSON.parse the raw response body. */
    return fetch(root + '?section_id=cart-market-data')
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var dataEl = tpl.content.querySelector('[data-cart-market-data]');
        return dataEl ? JSON.parse(dataEl.textContent) : null;
      })
      .catch(function () { return null; });
  }

  function cartSafetyCheck(countryCode) {
    /* Resolves { proceed, itemCount }. Fails open: an unreadable cart never blocks the
       switch — the backstop notice covers that path. */
    return fetchCartMarketData().then(function (data) {
      if (!data || !data.item_count) {
        return { proceed: true, itemCount: data ? data.item_count : 0 };
      }
      var dest = destinationMarketOf(countryCode);
      if (!dest) {
        /* Unresolved destination market: no way to evaluate exclusions — fail open. */
        return { proceed: true, itemCount: data.item_count };
      }
      var dropped = (data.lines || []).filter(function (line) {
        return (line.excluded || []).some(function (market) {
          return String(market).trim().toLowerCase() === dest;
        });
      });
      if (!dropped.length) {
        return { proceed: true, itemCount: data.item_count };
      }
      return showCartWarning(dropped).then(function (proceed) {
        return { proceed: proceed, itemCount: data.item_count };
      });
    });
  }

  function stashCartCount(itemCount) {
    if (!itemCount) return;
    writeStorage(window.sessionStorage, SWITCH_KEY, { count: itemCount, ts: Date.now() });
  }

  /* ---------------------------------------------------------------- localization submits */

  function returnPath() {
    /* Path without the current locale prefix — Shopify re-applies the destination
       market's prefix on redirect. The query string is preserved intact (UTM and other
       attribution params must survive a market switch); no parameters are stripped. */
    var path = window.location.pathname;
    var prefix = config.localeRootUrl;
    if (prefix && prefix !== '/' && path.indexOf(prefix) === 0) {
      path = path.slice(prefix.length) || '/';
    }
    return path + window.location.search;
  }

  /* ---------------------------------------------------------------- drawer form */

  var drawerForm = document.getElementById('market-drawer-form');
  var drawerCountry = document.querySelector('[data-market-drawer-country]');
  var drawerLanguages = document.querySelector('[data-market-drawer-languages]');
  var drawerLanguageList = document.querySelector('[data-market-drawer-language-list]');
  var drawerSummary = document.querySelector('[data-market-drawer-summary]');

  /* Pure filter over the Liquid-derived dataset: render the selected country's
     languages as radios. An empty (or missing) languages array is the single special
     case — hide the section entirely, so Continue submits country_code only and
     Shopify applies the destination market's default language. */
  function renderDrawerLanguages(code) {
    if (!drawerLanguages || !drawerLanguageList) return;
    var entry = countryEntry(code);
    var langs = config.showLanguages && entry && entry.languages ? entry.languages : [];
    drawerLanguageList.textContent = '';
    if (!langs.length) {
      drawerLanguages.hidden = true;
      return;
    }
    var current = (config.currentLanguage || '').toLowerCase();
    var offered = false;
    for (var i = 0; i < langs.length; i += 1) {
      if (langs[i].code.toLowerCase() === current) offered = true;
    }
    var chosen = offered ? current : langs[0].code.toLowerCase();
    langs.forEach(function (lang) {
      var label = document.createElement('label');
      label.className = 'market-drawer__language';
      var radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'locale_code';
      radio.value = lang.code;
      radio.checked = lang.code.toLowerCase() === chosen;
      var text = document.createElement('span');
      text.textContent = lang.name;
      label.appendChild(radio);
      label.appendChild(text);
      drawerLanguageList.appendChild(label);
    });
    drawerLanguages.hidden = false;
  }

  function updateDrawerSummary(code) {
    if (!drawerSummary || !config.strings.drawerSummary) return;
    var entry = countryEntry(code);
    if (!entry) return;
    /* "USD $" / "EUR €"; symbol suppressed when it just repeats the ISO code (e.g. CHF). */
    var currencyLabel = entry.symbol && entry.symbol !== entry.currency
      ? entry.currency + ' ' + entry.symbol
      : entry.currency;
    drawerSummary.textContent = config.strings.drawerSummary
      .split('[[country]]').join(entry.name)
      .split('[[currency]]').join(currencyLabel);
  }

  if (drawerCountry) {
    renderDrawerLanguages(drawerCountry.value);
    updateDrawerSummary(drawerCountry.value);
    drawerCountry.addEventListener('change', function () {
      renderDrawerLanguages(this.value);
      updateDrawerSummary(this.value);
    });
  }

  if (drawerForm) {
    var drawerSubmitting = false;
    drawerForm.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (drawerSubmitting) return;
      var selected = drawerCountry ? drawerCountry.value : config.currentCountry;
      var entry = countryEntry(selected);
      var radio = drawerForm.querySelector('input[name="locale_code"]:checked');
      var language = radio ? radio.value : null;
      /* Derived market comparison; a blank/unresolved market_code skips the cart
         pre-check (fail open) and submits — Shopify still routes correctly. */
      var marketChanged = !!(entry && entry.market && config.currentMarket && entry.market !== config.currentMarket);
      pushEvent('market_selector_drawer_confirm', {
        from_country: config.currentCountry,
        to_country: selected,
        to_language: language,
        market_changed: marketChanged
      });
      var check = marketChanged
        ? cartSafetyCheck(selected)
        : Promise.resolve({ proceed: true, itemCount: 0 });
      check.then(function (result) {
        if (!result.proceed) return;
        drawerSubmitting = true;
        persistChoice(selected, language);
        if (marketChanged) stashCartCount(result.itemCount);
        HTMLFormElement.prototype.submit.call(drawerForm);
      });
    });
  }

  function postLocalization(country, language) {
    var post = document.createElement('form');
    post.method = 'POST';
    post.action = root + 'localization';
    post.hidden = true;

    var fields = { _method: 'PUT', country_code: country, return_to: returnPath() };
    if (language) fields.locale_code = language;
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      post.appendChild(input);
    });

    document.body.appendChild(post);
    post.submit();
  }

  /* ---------------------------------------------------------------- post-switch backstop */

  function showNotice(text) {
    if (!notice) return;
    notice.textContent = '';

    var message = document.createElement('p');
    message.className = 'market-notice__message';
    message.textContent = text;
    notice.appendChild(message);

    var dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'market-notice__dismiss';
    dismissBtn.setAttribute('aria-label', config.strings.close);
    dismissBtn.textContent = '×';
    dismissBtn.addEventListener('click', function () {
      notice.hidden = true;
    });
    notice.appendChild(dismissBtn);

    notice.hidden = false;
  }

  function checkPostSwitchRemovals() {
    var stash = readStorage(window.sessionStorage, SWITCH_KEY);
    if (!stash) return;
    removeStorage(window.sessionStorage, SWITCH_KEY);
    if (!stash.count || !stash.ts || Date.now() - stash.ts > SWITCH_MAX_AGE_MS) return;
    fetch(root + 'cart.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart && cart.item_count < stash.count) {
          showNotice(config.strings.itemsRemoved);
        }
      })
      .catch(function () { /* ignore */ });
  }

  checkPostSwitchRemovals();

  /* ---------------------------------------------------------------- confirmation gate */

  var gateEventPayload = null;
  var gateCartCount = null;

  function dismissGate() {
    if (!gate || gate.hidden) return;
    gate.hidden = true;
    document.body.classList.remove('market-gate-open');
    persistChoice(config.currentCountry, null);
    if (gateEventPayload) pushEvent('market_recommendation_esc_dismiss', gateEventPayload);
  }

  function teardownGate(reason) {
    /* Failure exit: remove the gate and let the visitor continue browsing. */
    gate.hidden = true;
    document.body.classList.remove('market-gate-open');
    gateEventPayload = null;
    pushEvent('market_recommendation_teardown', { reason: reason });
  }

  function gateSuppressed() {
    if (!config.gateEnabled || config.isCart || !gate) return true;
    /* Server-rendered design-mode flag first (request.design_mode — immune to script
       ordering and window.Shopify clobbering); JS flag kept as belt-and-braces. */
    if (config.designMode) return true;
    if (window.Shopify && window.Shopify.designMode) return true;
    if (navigator.webdriver) return true;
    if (/bot|crawl|spider|slurp|lighthouse|headless|prerender/i.test(navigator.userAgent)) return true;
    try {
      if (window.localStorage.getItem(CHOICE_KEY)) return true;
    } catch (err) {
      return true; /* no storage means no way to honour a prior choice — stay quiet */
    }
    return false;
  }

  function showGate() {
    if (gateSuppressed()) return;

    var params = new URLSearchParams();
    params.set('country[enabled]', 'true');
    params.set('language[enabled]', 'true');
    if (config.currentCountry) params.set('country[exclude]', config.currentCountry);
    if (config.currentLanguage) params.set('language[exclude]', config.currentLanguage);

    fetch(root + 'browsing_context_suggestions.json?' + params.toString())
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        /* Mismatch-only hard bail-outs, all evaluated before any node is inserted:
           low confidence, unserved country, same market (a German visitor on /fr-eu is
           a country mismatch within the same market — say nothing), unreadable market
           metafield. The gate shell stays hidden and empty unless every check passes. */
        var suggestion = data && data.suggestions && data.suggestions[0];
        if (!suggestion || (suggestion.confidence || 0) < config.threshold) return;

        var countryPart = suggestion.parts && suggestion.parts.country;
        if (!countryPart || !countryPart.handle) return;
        if (countryPart.handle === config.currentCountry) return;
        if (!countryEntry(countryPart.handle)) return;
        if (!config.currentMarket) return;
        var detectedMarket = destinationMarketOf(countryPart.handle);
        if (!detectedMarket) return; /* unresolved market_code — fail quiet, no gate */
        if (detectedMarket === config.currentMarket) return;

        try {
          var select = gate.querySelector('[data-market-gate-select]');
          var confirmBtn = gate.querySelector('[data-market-gate-confirm]');
          var gateDialog = gate.querySelector('[role="dialog"]');
          if (!select || !confirmBtn || !gateDialog) return;

          /* The language part may be entirely absent; pass locale_code only on a confident
             suggestion, and only when the visitor confirms the detected country unchanged.
             An unsupported locale is safely ignored by Shopify (market default applies). */
          var languagePart = suggestion.parts && suggestion.parts.language;
          var language = languagePart && languagePart.handle && (languagePart.confidence || 0) >= config.threshold
            ? languagePart.handle
            : null;

          gateEventPayload = {
            detected_country: countryPart.handle,
            current_country: config.currentCountry,
            current_market: config.currentMarket || null,
            destination_country: countryPart.handle,
            destination_market: detectedMarket
          };

          /* Dropdown: every served country (from the suggestion's localized options map,
             restricted to the storefront's available set), pre-selected to the detection. */
          var handles = Object.keys(config.countries || {});
          var names = countryPart.options || {};
          handles
            .map(function (handle) {
              return { handle: handle, name: names[handle] || handle };
            })
            .sort(function (a, b) { return a.name.localeCompare(b.name); })
            .forEach(function (entry) {
              var option = document.createElement('option');
              option.value = entry.handle;
              option.textContent = entry.name + ' (' + config.countries[entry.handle].currency + ')';
              if (entry.handle === countryPart.handle) option.selected = true;
              select.appendChild(option);
            });

          /* Never reveal a broken gate. */
          if (!select.options.length) {
            teardownGate('empty_country_list');
            return;
          }

          /* One non-blocking cart read at reveal time, solely to seed the post-switch
             backstop stash. The confirm path itself performs no fetch. Liquid knows
             cart.item_count at render time, but rendered HTML can be stale (cached
             pages, Ajax adds after render), so the count is read live here instead. */
          fetch(root + 'cart.js')
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (cart) { gateCartCount = cart ? cart.item_count : null; })
            .catch(function () { gateCartCount = null; });

          confirmBtn.addEventListener('click', function () {
            try {
              var selected = select.value;
              var payload = {};
              for (var key in gateEventPayload) {
                if (Object.prototype.hasOwnProperty.call(gateEventPayload, key)) payload[key] = gateEventPayload[key];
              }
              var selectedMarket = destinationMarketOf(selected);
              payload.destination_country = selected;
              payload.destination_market = selectedMarket;

              /* Confirming a country that resolves to the CURRENT market must not POST or
                 reload: write the suppress key and close. An unresolved market POSTs —
                 Shopify still routes correctly (fail open). */
              if (selectedMarket && selectedMarket === config.currentMarket) {
                gate.hidden = true;
                document.body.classList.remove('market-gate-open');
                gateEventPayload = null;
                persistChoice(selected, null);
                pushEvent('market_recommendation_accept', payload);
                return;
              }

              pushEvent('market_recommendation_accept', payload);
              var chosenLanguage = selected === countryPart.handle ? language : null;
              gateEventPayload = null;
              persistChoice(selected, chosenLanguage);
              stashCartCount(gateCartCount);
              postLocalization(selected, chosenLanguage);
            } catch (err) {
              teardownGate('confirm_error');
            }
          });

          gate.hidden = false;
          document.body.classList.add('market-gate-open');
          gateDialog.focus();
          pushEvent('market_recommendation_impression', gateEventPayload);
        } catch (err) {
          teardownGate('build_error');
        }
      })
      .catch(function () { /* stay quiet on failure */ });
  }

  showGate();
})();
